import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import StepList from '../../components/marketing/StepList';
import TrustBadges from '../../components/marketing/TrustBadges';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './Business.css';

// BestHand for Business — ADDITIVE B2B lead-capture landing.
// HONESTY: this describes the OFFERING / benefits for business customers. There is NO live business
// portal, dashboard or login yet — do NOT imply one exists. Primary action is "Talk to us" -> /contact-us.
// LEGAL_REVIEW_REQUIRED: value-prop, billing and support claims below are marketing copy and must be
// reviewed by counsel before production. Do not present any benefit as a contractual guarantee.

// COMPLIANCE: keep facts consistent with confirmed inputs — vetted (background-checked) pros,
// secure cashless payments via Stripe, percentage-only fees. No platform-insurance claims.
// COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.

const VALUE = [
  {
    icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6l-8-4ZM9 12l2 2 4-4',
    title: 'Vetted pros',
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    text: 'Every pro is background-checked before they take a job — the same trusted network your tenants and customers rely on.',
  },
  {
    icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 11h.01M15 11h.01',
    title: 'One account, many sites',
    text: 'Manage work across multiple offices, units or properties from a single business relationship — no juggling separate accounts.',
  },
  {
    icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z',
    title: 'Consolidated billing',
    text: 'Keep your bookkeeping simple with clear, itemized statements across all your locations and jobs.',
  },
  {
    icon: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    title: 'Priority support',
    text: 'A direct line to our team for scheduling, follow-ups and anything that comes up across your portfolio.',
  },
];

const STEPS = [
  {
    icon: 'M8 2v4M16 2v4M3 10h18M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
    title: 'Tell us about your needs',
    text: 'Share your locations, the kinds of jobs you run, and how often you need help.',
  },
  {
    icon: 'M20 6 9 17l-5-5',
    title: 'We set you up',
    text: 'Our team helps you get started with the right access, billing and points of contact for your business.',
  },
  {
    icon: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.1-2.1 2.6-2.4Z',
    title: 'Book trusted pros',
    text: 'Request vetted pros for any of your sites and track jobs to completion — with support a message away.',
  },
];

const TRUST = [
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  { icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6l-8-4ZM9 12l2 2 4-4', title: 'Background-checked pros', text: 'A background check before any pro takes a job.' },
  { icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z', title: 'Secure payments', text: 'Cashless, in-app payments powered by Stripe — no cash changing hands on site.' },
  { icon: 'M9 12l2 2 4-4M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'Industry-low fees', text: 'Transparent, percentage-based fees — more of every dollar stays where it should.' },
  { icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', title: 'Priority support', text: 'A responsive team to help you keep every location running smoothly.' },
];

const USE_CASES = [
  { title: 'Offices', text: 'Furniture assembly, mounting, small repairs and turnaround help — without managing a vendor list.' },
  { title: 'Property managers', text: 'Make-readies, repairs and ongoing maintenance across a portfolio of units.' },
  { title: 'Landlords', text: 'Reliable pros for tenant requests and unit upkeep, booked when you need them.' },
  { title: 'Small businesses', text: 'On-demand help for storefronts and workspaces, from installs to fixes.' },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Home and property services for business',
  provider: { '@type': 'Organization', name: 'BestHand' },
  areaServed: 'US',
  description:
    'BestHand for Business connects offices, property managers, landlords and small businesses with vetted local pros, with one account for multiple sites, consolidated billing and priority support.',
};

function Business() {
  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="BestHand for Business"
        title="Trusted pros for every location you manage."
        subtitle="Vetted local pros, one account for multiple sites, consolidated billing and priority support — built for offices, property managers, landlords and small businesses."
        primaryLabel="Talk to us"
        primaryTo="/contact-us"
        secondaryLabel="See how it works"
        secondaryTo="/how-it-works"
        badges={['Vetted pros', 'One account', 'Priority support']}
      />

      {/* Value props */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Why teams choose BestHand</h2>
          <p className={s.lead}>One trusted network for all your locations — without the overhead of managing vendors yourself.</p>
          <div className={s.grid}>
            {VALUE.map((v) => (
              <div className={s.card} key={v.title}>
                <span className={s.cardIc}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={v.icon} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h3 className={s.cardTitle}>{v.title}</h3>
                <p className={s.cardText}>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Built for the way you work</h2>
          <p className={s.lead}>Whatever you manage, BestHand brings reliable help to every door.</p>
          <div className={s.useGrid}>
            {USE_CASES.map((u) => (
              <div className={s.useCard} key={u.title}>
                <h3 className={s.useTitle}>{u.title}</h3>
                <p className={s.useText}>{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works for business */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <StepList heading="Getting started is simple" steps={STEPS} />
        </div>
      </section>

      {/* Trust band */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="The trust you’d expect" items={TRUST} />
        </div>
      </section>

      {/* Lead capture / talk to us */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.contactCard}>
            <div>
              <h2 className={s.h2Left}>Let’s talk about your business</h2>
              <p className={s.contactText}>
                Tell us about your locations and the work you need done, and our team will help you get set up.
                {/* HONESTY: no self-serve business portal yet — this routes to our team. */}
              </p>
            </div>
            <div className={s.contactActions}>
              <Link to="/contact-us" className={s.contactBtn}>Talk to us</Link>
              <a href="mailto:business@besthandapp.com" className={s.contactBtnAlt}>business@besthandapp.com</a>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Reliable help for every location."
        subtitle="Vetted pros, one account, priority support — let’s get you set up."
        primaryLabel="Talk to us"
        primaryTo="/contact-us"
        secondaryLabel="Browse services"
        secondaryTo="/services"
      />
    </main>
  );
}

export default withStyles(s)(Business);
